/**
 * fuzzySearch.js — Iter 189d Phase C · Library UX polish
 *
 * A tiny fuzzy matcher (no external dep). Scores each candidate against a
 * lowercase query using three signals combined:
 *   1. exact substring match       → +100
 *   2. prefix match                 → +40
 *   3. character subsequence match  → +5 per matched char, with adjacency bonus
 *
 * `fuzzyFilter(items, getText, query)` returns items sorted by relevance
 * descending. Zero-score items are dropped. Non-string haystacks are safely
 * coerced.
 */

/**
 * Score a single haystack against the (already lowercased) query.
 * Returns a positive number if matched, 0 otherwise.
 */
function scoreOne(hayLower, qLower) {
  if (!qLower) return 1;
  if (!hayLower) return 0;

  // Substring / prefix boosts
  const idx = hayLower.indexOf(qLower);
  if (idx === 0) return 200;                    // strong prefix win
  if (idx > 0)   return 100 + Math.max(0, 40 - idx); // earlier = better

  // Character subsequence
  let score = 0;
  let hi = 0;
  let lastMatched = -2;
  for (let qi = 0; qi < qLower.length; qi++) {
    const ch = qLower.charCodeAt(qi);
    let found = -1;
    while (hi < hayLower.length) {
      if (hayLower.charCodeAt(hi) === ch) { found = hi; hi++; break; }
      hi++;
    }
    if (found < 0) return 0;                    // any missing char fails
    score += 5;
    if (found === lastMatched + 1) score += 3;  // adjacency bonus
    lastMatched = found;
  }
  return score;
}

/**
 * Score an item using multiple haystacks. Best haystack score wins.
 */
function scoreItem(hays, qLower) {
  let best = 0;
  for (const h of hays) {
    const s = scoreOne(String(h || '').toLowerCase(), qLower);
    if (s > best) best = s;
  }
  return best;
}

/**
 * Filter + rank an array of items by fuzzy relevance against `query`.
 *
 * @param {Array<T>} items
 * @param {(item:T)=>string|string[]} getText — haystacks. Return a single
 *   string or an array of strings; array signals "search across all".
 * @param {string} query — user input, case-insensitive.
 * @returns {Array<T>} — matched items, most-relevant first.
 */
export function fuzzyFilter(items, getText, query) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return items.slice();
  const scored = [];
  for (const item of items) {
    const raw = getText(item);
    const hays = Array.isArray(raw) ? raw : [raw];
    const s = scoreItem(hays, q);
    if (s > 0) scored.push([s, item]);
  }
  scored.sort((a, b) => b[0] - a[0]);
  return scored.map(([, item]) => item);
}
